import { useTranslation } from "react-i18next";
import {
  createPath,
  Navigate,
  Route,
  useLocation,
  useParams,
} from "react-router-dom";
import { lazy, useEffect } from "react";
import {
  localeRedirects,
  RequiresLogin,
  SentryRoutes,
  suspense,
} from "../../../../../src/components/AppRoutes";
import { editorOidcAuthKey as authKey } from "../../utils/oidcConfig";
import UnableToAccess from "../../../../../src/components/UnableToAccess/UnableToAccess";

const Callback = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/containers/Callback"
    ),
);
const UserLogin = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/containers/UserLogin"
    ),
);
const GoogleOAuthCallback = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/containers/GoogleOAuthCallback"
    ),
);
const SilentRenew = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/SilentRenew/SilentRenew"
    ),
);
const FrontChannelLogout = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/FrontChannelLogout/FrontChannelLogout"
    ),
);
const UserTypeLogin = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/UserTypeLogin/UserTypeLogin"
    ),
);
const GlobalLayout = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/GlobalLayout/GlobalLayout"
    ),
);
const LandingPage = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/LandingPage/LandingPage"
    ),
);

const EducationLandingPage = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/EducationLandingPage/EducationLandingPage"
    ),
);
const ProjectLayout = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/ProjectLayout/ProjectLayout"
    ),
);
const ProjectIndex = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/ProjectIndex/ProjectIndex"
    ),
);

const AuthenticatedProjectIndex = RequiresLogin(ProjectIndex);
const ProjectComponentLoader = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/containers/ProjectComponentLoader"
    ),
);
const EmbeddedViewer = lazy(
  () =>
    import(
      /* webpackPrefetch: true */ "../../../../../src/components/EmbeddedViewer/EmbeddedViewer"
    ),
);

const projectLinkRedirects = [
  "/null/projects/:identifier",
  "/projects/:identifier",
];

const ProjectsRedirect = () => {
  const { identifier } = useParams();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  return <Navigate replace to={`/${locale}/projects/${identifier}`} />;
};

const CodeClassroomRedirect = () => {
  const location = useLocation();
  const fullPath = createPath(location);

  useEffect(() => {
    window.location.href = `${process.env.REACT_APP_CODE_CLASSROOM_URL}${fullPath}`;
  }, [fullPath]);

  return null;
};

export const EditorAppRoutes = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language;

  return (
    <SentryRoutes>
      <Route path="auth">
        <Route path="callback" element={suspense(<Callback />)} />
        <Route path="silent_renew" element={suspense(<SilentRenew />)} />
        <Route
          path="frontchannel_logout"
          element={suspense(<FrontChannelLogout />)}
        />
        <Route path="user_login/:userType" element={suspense(<UserLogin />)} />
        <Route
          path="google/callback"
          element={suspense(<GoogleOAuthCallback />)}
        />
      </Route>

      <Route
        path={":locale"}
        element={suspense(<GlobalLayout authKey={authKey} />)}
      >
        <Route path="login" element={suspense(<UserTypeLogin />)} />
        <Route index element={suspense(<LandingPage />)} />
        <Route path="*" element={suspense(<UnableToAccess />)} />
        <Route path="error" element={suspense(<UnableToAccess />)} />
        <Route path={"projects"} element={suspense(<ProjectLayout />)}>
          <Route index element={suspense(<AuthenticatedProjectIndex />)} />
          <Route
            path={":identifier"}
            element={suspense(<ProjectComponentLoader authKey={authKey} />)}
          />
        </Route>

        <Route
          path="embed/viewer/:identifier"
          element={suspense(<EmbeddedViewer authKey={authKey} />)}
        />

        <Route path="education" element={suspense(<EducationLandingPage />)} />

        <Route path="school/*" element={<CodeClassroomRedirect />} />
        <Route path="schools/*" element={<CodeClassroomRedirect />} />
        <Route path="invitations/*" element={<CodeClassroomRedirect />} />
        <Route path="lesson/*" element={<CodeClassroomRedirect />} />
        <Route
          path="create-account-info/*"
          element={<CodeClassroomRedirect />}
        />
      </Route>

      {projectLinkRedirects.map((link) => {
        return <Route key={link} path={link} element={<ProjectsRedirect />} />;
      })}

      {localeRedirects.map((link) => {
        return (
          <Route
            key={link}
            path={link}
            element={<Navigate replace to={`/${locale}${link}`} />}
          />
        );
      })}
    </SentryRoutes>
  );
};
